import axios from "axios";
import { getSessionToken, clearSessionToken } from "./sessionStore";

const BASE = process.env.REACT_APP_BACKEND_URL;

export const API = `${BASE}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  // Default timeout — without this, slow/hanging backends leave the UI
  // stuck on "Loading…" forever (no rejection ever fires, so LoadingState's
  // retry button never appears). Tester complaint v37: "after I finish a
  // quick session everything freezes and loading appears on all pages".
  // Root cause: /game/complete triggers server-side step-sync + profile
  // recompute, which occasionally takes 10-30s. Subsequent calls queued
  // behind it hung. 20s gives slow networks room but still surfaces a
  // clear error within a reasonable wait.
  timeout: 20000,
});

// Attach session_token in header as fallback (cookies are primary on web,
// but Capacitor WebView can't share cookies with the backend domain so the
// Bearer header is the actual auth on Android).
api.interceptors.request.use((cfg) => {
  const token = getSessionToken();
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  // Iter41 — surface the client's TZ offset so the backend can bucket
  // "today" against LOCAL midnight (streaks, daily-goal counter,
  // daily-workout reset, etc.) instead of UTC. JS getTimezoneOffset()
  // returns minutes BEHIND UTC (UTC+2 → -120). Recomputed per-request
  // so DST transitions and time-travel on the device are picked up.
  try {
    cfg.headers["X-Timezone-Offset"] = String(new Date().getTimezoneOffset());
  } catch (_) {
    // Defensive — if for some reason `Date` is borked, just skip.
  }
  return cfg;
});

// URLs allowed to legitimately return 401 (login attempts, /me probe on boot,
// session exchange). For these we DON'T force a logout/redirect — the
// caller (AuthContext.refresh / Login.handleSubmit) handles the failure
// gracefully.
const AUTH_FREE_PATHS = [
  "/auth/me",
  "/auth/login",
  "/auth/signup",
  "/auth/session",
  "/auth/reset-password",
  "/auth/logout",
];

// Response interceptor: when ANY authenticated endpoint returns 401 mid-
// session (token expired / session deleted server-side / db wiped), clear
// the stored token and bounce the user to /login. Without this, pages get
// trapped in a "Try Again" loop because axios just keeps re-sending the
// same dead Bearer header on retry.
//
// Tester complaint v47: "every tab shows Try Again on my phone". Backend
// logs confirmed: /api/journey, /api/challenges, /api/leaderboard/global
// all returning 401 with the same expired token. Now we surface that to
// the user via re-auth instead of a silent loop.
let redirecting = false;
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    if (status === 401 && !AUTH_FREE_PATHS.some((p) => url.includes(p))) {
      if (!redirecting && typeof window !== "undefined") {
        redirecting = true;
        // Surface in the Debug page log so testers can confirm this fired.
        try { console.warn("[auth] 401 on", url, "— clearing token + redirecting to /login"); } catch (_) {}
        // Fire-and-forget — don't block the rejection chain.
        clearSessionToken().catch(() => {});
        const path = window.location.pathname || "";
        if (!path.startsWith("/login") && path !== "/") {
          // hard redirect so all in-flight 401s bail out together
          window.location.replace("/login?expired=1");
        } else {
          // Reset flag if we're already on login so future 401s can redirect
          setTimeout(() => { redirecting = false; }, 1000);
        }
      }
    }
    return Promise.reject(error);
  },
);
